import { isObject, mergeWith } from 'lodash';
import type { InteractionContact } from '@/common/types/interaction';
import type {
  AgentBrokerage,
  AgentContact,
  BaseContact,
  ClientContact,
  ContactsFormData,
  ContactType,
} from './types';

export type ContactRow = ContactsFormData['contacts'][ContactType][number];

type ContactOfType<T extends ContactType> = Extract<
  InteractionContact,
  { type: T }
>;

const emptyBrokerage = (): AgentBrokerage => ({
  companyName: null,
  licenseNumber: null,
  mlsCode: null,
  mlsId: null,
  officeEmail: null,
  street1: null,
  street2: null,
  city: null,
  state: null,
  zip: null,
});

const emptyBase = (): BaseContact => ({
  firstName: null,
  lastName: null,
  email: null,
  cellPhone: null,
  homePhone: null,
  companyName: null,
  nickname: null,
  role: null,
  escrowNumber: null,
  titleNumber: null,
  confirmationNumber: null,
  googleContactId: null,
  followUpBossContactId: null,
  contactSource: null,
  street1: null,
  street2: null,
  city: null,
  state: null,
  zip: null,
  county: null,
});

const emptyAgent = (): AgentContact => ({
  ...emptyBase(),
  licenseNumber: null,
  mlsId: null,
  primaryMls: null,
  userId: null,
  teamId: null,
  brokerage: emptyBrokerage(),
});

const emptyClient = (): ClientContact => ({
  ...emptyBase(),
  nickname: null,
  entityType: 'individual',
  ownershipStatus: null,
  isSuccessor: null,
  isSelfRepresented: null,
  trust: { trustName: null, title: null, titleOther: null },
  entity: { entityName: null, title: null, titleOther: null },
  probate: { estateName: null, title: null, titleOther: null },
  powerOfAttorney: { firstName: null, lastName: null, email: null },
});

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || value === '';

export function fillBlankFields<T>(row: T, incoming: ContactRow): T {
  return mergeWith({}, incoming, row, (incomingValue, rowValue) => {
    if (isObject(incomingValue) || isObject(rowValue)) return undefined;
    return isBlank(rowValue) ? incomingValue : rowValue;
  });
}

export type ContactMinRows = Partial<Record<ContactType, number>>;

type ContactBuckets = ContactsFormData['contacts'];

// The four primary contacts (own-side + counterparty agent/client) always get
// one row so the form renders an editable card immediately. Callers extend this
// with the third-party roles their market always offers.
const PRIMARY_MIN_ROWS: ContactMinRows = {
  agent: 1,
  client: 1,
  counterpartyAgent: 1,
  counterpartyClient: 1,
};

const EMPTY_ROW: { [T in ContactType]: () => ContactBuckets[T][number] } = {
  agent: emptyAgent,
  client: emptyClient,
  counterpartyAgent: emptyAgent,
  counterpartyClient: emptyClient,
  counterpartyTransactionCoordinator: emptyBase,
  titleOfficer: emptyBase,
  escrowOfficer: emptyBase,
  lender: emptyBase,
  appraiser: emptyBase,
  attorney: emptyBase,
  counterpartyAttorney: emptyBase,
  closingAttorney: emptyBase,
  propertyManagingAgent: emptyBase,
  homeWarranty: emptyBase,
  other: emptyBase,
};

// Rows hydrate as the raw server records grouped by `type` — no per-field
// mapping, since the form binds to the contact shape directly — then each role
// is padded with empty rows up to its minimum.
export function hydrateContacts(
  contacts: readonly InteractionContact[],
  minRows: ContactMinRows = {},
): ContactsFormData {
  const min = { ...PRIMARY_MIN_ROWS, ...minRows };

  const padded = <T extends ContactType>(type: T): ContactBuckets[T] => {
    const existing = contacts
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .filter((c): c is ContactOfType<T> => c.type === type);
    const missing = (min[type] ?? 0) - existing.length;
    const empties = Array.from(
      { length: Math.max(missing, 0) },
      EMPTY_ROW[type],
    );
    return [...existing, ...empties] as ContactBuckets[T];
  };

  return {
    contacts: {
      agent: padded('agent'),
      client: padded('client'),
      counterpartyAgent: padded('counterpartyAgent'),
      counterpartyClient: padded('counterpartyClient'),
      counterpartyTransactionCoordinator: padded(
        'counterpartyTransactionCoordinator',
      ),
      titleOfficer: padded('titleOfficer'),
      escrowOfficer: padded('escrowOfficer'),
      lender: padded('lender'),
      appraiser: padded('appraiser'),
      attorney: padded('attorney'),
      counterpartyAttorney: padded('counterpartyAttorney'),
      closingAttorney: padded('closingAttorney'),
      propertyManagingAgent: padded('propertyManagingAgent'),
      homeWarranty: padded('homeWarranty'),
      other: padded('other'),
    },
  };
}

export { emptyAgent, emptyBase, emptyClient };
